import { publicCatalogApi } from '@/features/publicsite/catalog/api/publicCatalogApi';
import { CatalogCard } from '@/features/publicsite/catalog/CatalogCard';
import { CatalogSection } from '@/features/publicsite/catalog/CatalogSection';
import type { ResearchOrganization } from '@/features/publicsite/types/research/ResearchOrganization';
import { usePublicList } from '@/features/publicsite/usePublicList';
import { publicCatalog } from '@/i18n/dictionaries/publicCatalog';
import { useTranslation } from '@/i18n/useTranslation';

/**
 * The research institutions published on the platform (§6.2).
 *
 * Until this page the public zone named an organisation only in passing — a line under a project's
 * title — and had nowhere to say what it does. Its expertise, its infrastructure and its contact
 * were written at registration and read back by nothing.
 *
 * The card leads with what the institution has in the catalogue, because that is the reason to open
 * its profile: an organisation with no projects and no results is a name, and the counts say so
 * before the click rather than after.
 */
export function ResearchOrganizationsPage() {
  const { t } = useTranslation(publicCatalog);
  const organizations = usePublicList<ResearchOrganization>(
    publicCatalogApi.researchOrganizationsUrl,
  );

  return (
    <CatalogSection
      eyebrow={t('eyebrowCatalog')}
      heading={t('orgsHeading')}
      lead={t('orgsLead')}
      items={organizations}
      renderCard={(o) => (
        <CatalogCard
          key={o.id}
          id={`organizatie-${o.id}`}
          to={`/cercetare/organizatii/${o.id}`}
          badge={t('orgProjects', { count: o.projectCount })}
          title={o.name}
          // Empty rather than null where the institution listed none, so this is a join over
          // nothing and the subtitle simply does not render.
          subtitle={o.expertiseAreas.join(', ') || null}
        >
          <p className="muted">{t('orgResults', { count: o.resultCount })}</p>
        </CatalogCard>
      )}
    />
  );
}
